import React, { Component } from 'react';
import { Select, TextField, Typography, Button, FormControlLabel, Toolbar, Radio, RadioGroup, Dialog, DialogContent, DialogActions, MenuItem } from '@mui/material';
import Config from '../../config';
import axios from 'axios';
import { DatabaseStorageHelper } from '../../helpers';
import Auth from '../../auth';

export type ProfileType = 'Export' | 'Import' | 'Message' | string;

export interface ProfileField {
    field?: any;
    alias?: any;
    index?: any;
    id?: any;
    [key: string]: any;
}

// Profiles are assembled imperatively and carry a varying set of optional
// fields depending on profileType. Other modules pass their own profile shapes
// (ExportProfile, RemoteProfile) into these helpers, so every known field is
// typed loosely to stay structurally compatible with those callers.
export interface Profile {
    Name?: any;
    DocumentType?: any;
    Fields?: any;
    Filter?: any;
    Rules?: any;
    Partner?: any;
    Subject?: any;
    Body?: any;
    Id?: any;
    PartnerCompanyName?: any;
    Shared?: any;
    Description?: any;
    [key: string]: any;
}

interface ProfileMeta {
    Id?: any;
    Name?: any;
    Type?: ProfileType;
    Profile?: any;
    Shared?: any;
}

const addProfile = (profiles: Profile[], name: string, fields: any[], docType: string, filters?: any, meta?: any, profileType?: ProfileType, rules?: any, partner?: any) => {
    let newProfile: Profile = {Name: name, Fields: (fields || []).map((field, i) => {
        if(typeof field === "string"){
            return {field: field, index: i + 1};
        }
        return {...field, index: i + 1};
    }), DocumentType: docType};
    if(profileType === 'Export'){
        let defaultFilter = [{field: 'Status', operator: 'eq', value: "Submitted"}];
        if(docType === 'Order'){
            defaultFilter = [{field: 'Status', operator: 'eq', value: "Ready to Ship"}]
        }
        newProfile.Filter = filters && filters.length > 0 ? filters : defaultFilter;
    } else if (profileType !== "Import"){
        newProfile.Filter = filters;
    }
    if(rules){
        newProfile.Rules = rules;
    }
    if(partner){
        newProfile.Partner = partner;
    }
    if(meta){
        newProfile.Id = meta.Id;
        newProfile.PartnerCompanyName = meta.PartnerCompanyName;
        newProfile.Shared = meta.Shared;
    }
    profiles.push(newProfile);
}

const addExportProfile = (profiles: Profile[], name: string, fields: any[], docType: string, filters?: any, meta?: any) => {
    addProfile(profiles, name, fields, docType, filters, meta, 'Export');
}

const loadProfiles = async (profileType: ProfileType, defaultProfiles?: Profile[]) => {
    var profileRes = (await axios.get(Config.api + `/odata/Company/Functions.GetUserDataStartsWith?name=${profileType.toLowerCase()}Profile-&userid=${Auth.getUserId()}`)).data.value;
    var profiles: Profile[] = [];
    for(const savedProfile of profileRes){
        let parsed = JSON.parse(savedProfile.Value);
        addProfile(profiles, savedProfile.Key.substring(profileType.length + 8), parsed.Fields, parsed.DocumentType, parsed.Filter, null, profileType, parsed.Rules, parsed.Partner);
    }
    let companyPartnerProfileRes = (await axios.get(Config.api + `/odata/Company/VwImportExportProfiles?$filter=Type eq '${profileType}'`)).data.value;
    for(const savedProfile of companyPartnerProfileRes){
        let parsed = JSON.parse(savedProfile.Profile);
        addProfile(profiles, savedProfile.Name, parsed.Fields, parsed.DocumentType, parsed.Filter, savedProfile, profileType, parsed.Rules, parsed.Partner);
    }
    profiles = profiles.concat(defaultProfiles || []);
    return profiles;
}

const loadProfilesWithDescriptions = async (profileType: ProfileType, defaultProfiles?: Profile[]) => {
    let profiles = await loadProfiles(profileType, defaultProfiles);
    profiles.forEach(profile => {
        if(profile.PartnerCompanyName){
            profile.Description = `This profile is provided by ${profile.PartnerCompanyName}.`;
        } else if(profile.Id && !profile.Shared){
            profile.Description = 'This profile is visible to all users for this company.';
        } else if (profile.Shared){
            profile.Description = 'This profile is visible to all users for this company and all trading partners.';
        } else if (defaultProfiles && defaultProfiles.some(r => r.Name === profile.Name)){
            profile.Description = 'This is a default profile provided by Logicbroker.';
        } else {
            profile.Description = 'This profile is only visible to you.';
        }
    });
    return profiles;
}

const getProfile = (values: Profile, profileType: ProfileType): Profile => {
    let profile: Profile = {};
    profile.DocumentType = values.DocumentType;
    if(profileType === 'Export'){
        profile.Filter = values.Filter;
        profile.Fields = values.Fields.map(r => {return {field: r.field, id: r.index, alias: r.alias}});
    } else if (profileType === 'Import'){
        profile.Fields = values.Fields.filter(r => r.alias && r.field !== r.alias).map((r, i) => {return {field: r.field, id: i, alias: r.alias}});
    } else if (profileType === 'Message') {
        profile.Subject = values.Subject;
        profile.Body = values.Body;
    } else {
        profile.Fields = values.Fields.map((r, i) => {return {...r, id: i}});
        profile.Filter = values.Filter;
        profile.Rules = values.Rules;
        profile.Partner = values.Partner;
    }
    return profile;
}

export const save = async (values: Profile, profileType: ProfileType) => {
    let key = profileType.toLowerCase() + 'Profile-' + values.Name;
    if(values.Id){
        var profileMeta: ProfileMeta = {Id: values.Id, Name: values.Name, Type: profileType};
        var profile = getProfile(values, profileType);
        profileMeta.Profile = JSON.stringify(profile);
        profileMeta.Shared = values.Shared;
        await axios.put(Config.api + '/odata/company/VwImportExportProfiles', profileMeta);
    }else{
        await new DatabaseStorageHelper().set(key, getProfile(values, profileType));
    }
}

const deleteProfile = async (values: Profile, profileType: ProfileType) => {
    let key = profileType.toLowerCase() + 'Profile-' + values.Name;
    if(values.Id){
        await axios.delete(Config.api + `/odata/company/VwImportExportProfiles(${values.Id})`);
    }else{
        await new DatabaseStorageHelper().remove(key);
    }
}

const saveAsSharedProfile = async (values: Profile, profileType: ProfileType, shareWithPartners?: boolean) => {
    let profileMeta: ProfileMeta = {Id: values.Id, Name: values.Name, Type: profileType};
    let profile = getProfile(values, profileType);
    profileMeta.Profile = JSON.stringify(profile);
    profileMeta.Shared = !(!shareWithPartners);
    if(profileMeta.Id) {
        await axios.put(Config.api + '/odata/company/VwImportExportProfiles', profileMeta);
    } else {
        await axios.put(Config.api + '/odata/company/VwImportExportProfiles', profileMeta);
        let key = profileType.toLowerCase() + 'Profile-' + profileMeta.Name;
        await new DatabaseStorageHelper().remove(key);
    }
}

interface DialogTemplateArgs {
    [key: string]: any;
}

interface ShareDialogProps {
    isRetailer?: boolean;
    open?: boolean;
    onClose?: (event?: any, reason?: string) => void;
    onShare?: (name?: string) => void;
    profileType?: ProfileType;
    values?: Profile;
    template?: (args: DialogTemplateArgs) => React.ReactElement | null;
}

interface ShareDialogState {
    shareLevel: string;
    disableShare: boolean;
}

class ShareDialog extends Component<ShareDialogProps, ShareDialogState> {
    state: ShareDialogState = {shareLevel: 'company', disableShare: false};

    constructor(props: ShareDialogProps) {
        super(props);
        this.share = this.share.bind(this);
        this.setState = this.setState.bind(this);
    }

    componentDidUpdate(prevProps: ShareDialogProps){
        if((prevProps.values || {}).Shared !== (this.props.values || {}).Shared){
            if((this.props.values || {}).Shared){
                this.setState({shareLevel: 'partner'});
            } else if (this.state.shareLevel === 'partner'){
                this.setState({shareLevel: 'company'});
            }
        }
        if(!prevProps.open && this.props.open && this.state.disableShare){
            this.setState({disableShare: false});
        }
    }
    share = async () => {
        let {profileType, onShare, values} = this.props;
        this.setState({disableShare: true});
        await saveAsSharedProfile(values, profileType, this.state.shareLevel === "partner");
        onShare(values.Name);
    }
    render(){
        let { isRetailer, open, onClose } = this.props;
        let disabled = this.state.disableShare;

        if(this.props.template)
            return this.props.template({isRetailer, open, onClose, disabled, state: this.state, setState: this.setState, share: this.share});

        return (
            <Dialog
                maxWidth="sm"
                open={open}
                onClose={(event, reason) => {
                  if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    if (disabled) return;
                  }
                  onClose(event, reason);
                }}>
                                <Toolbar className='lbtoolbar'>Share Export Profile</Toolbar>
                                {isRetailer ? <><DialogContent>
                                  <Typography>Who should be allowed to access this profile?</Typography>
                                  <RadioGroup {...({disabled} as any)} value={this.state.shareLevel} onChange={(e) => this.setState({shareLevel: e.target.value})}>
                                      <FormControlLabel value={'company'} control={<Radio />} label='All users from my company.'/>
                                      <FormControlLabel value={'partner'} control={<Radio />} label='All users from my company and all trading partners. Trading partners will not be able to change the profile.'/>
                                  </RadioGroup>
                                </DialogContent>
                                <DialogActions>
                                  <Button onClick={onClose} disabled={disabled} variant="text">
                                    Cancel
                                  </Button>
                                  <Button onClick={this.share} disabled={disabled} variant="text" color="primary">
                                    Share
                                  </Button>
                                </DialogActions></> :
                                <><DialogContent>
                                  <Typography>Do you want to share this profile with other users at your company?</Typography>
                                </DialogContent>
                                <DialogActions>
                                  <Button onClick={onClose} disabled={disabled} variant="text" color="primary">
                                    No
                                  </Button>
                                  <Button onClick={this.share} disabled={disabled} variant="text" color="primary">
                                    Yes
                                  </Button>
                                </DialogActions></>
                            }
                              </Dialog>
        );
    }
}

interface DeleteDialogProps {
    open?: boolean;
    onClose?: (event?: any, reason?: string) => void;
    onDelete?: () => void;
    values?: Profile;
    type?: string;
    profileType?: ProfileType;
    template?: (args: DialogTemplateArgs) => React.ReactElement | null;
}

interface DeleteDialogState {
    disableDelete: boolean;
}

class DeleteDialog extends Component<DeleteDialogProps, DeleteDialogState> {
    state: DeleteDialogState = {disableDelete: false};

    constructor(props: DeleteDialogProps) {
        super(props);
        this.delete = this.delete.bind(this);
    }

    componentDidUpdate(prevProps: DeleteDialogProps){
        if(!prevProps.open && this.props.open && this.state.disableDelete){
            this.setState({disableDelete: false});
        }
    }
    delete = async () => {
        this.setState({disableDelete: true});
        let {values, profileType} = this.props;
        try{
            await deleteProfile(values, profileType);
        }catch{
            // do nothing
        }
        this.props.onDelete();
    }
    render(){
        let { open, onClose, values, type } = this.props;
        let disabled = this.state.disableDelete;
        type = type || 'profile';
        if(this.props.template)
            return this.props.template({values, disabled, open, onClose, deleteFnc: this.delete});

        return (
            <Dialog
                maxWidth="sm"
                open={open}
                onClose={(event, reason) => {
                  if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    if (disabled) return;
                  }
                  onClose(event, reason);
                }}>
                <Toolbar className='lbtoolbar'>Confirm Delete</Toolbar>
                <DialogContent>
                {values.Id && !values.Shared && <Typography>This {type} is visible to all users at your company.</Typography>}
                {values.Id && values.Shared && <Typography>This {type} is visible to all users at your company and all trading partners.</Typography>}
                  <Typography>Are you sure you want to delete this {type}?</Typography>
                </DialogContent>
                <DialogActions>
                  <Button onClick={onClose} disabled={disabled} variant="text" color="primary">
                    No
                  </Button>
                  <Button onClick={this.delete} disabled={disabled} variant="text" color="primary">
                    Yes
                  </Button>
                </DialogActions>
              </Dialog>
        );
    }
}

interface SaveDialogProps {
    open?: boolean;
    onClose?: (event?: any, reason?: string) => void;
    onSave?: () => void;
    values?: Profile;
    type?: string;
    profileType?: ProfileType;
    format?: (values: Profile) => Profile;
    shared?: boolean;
    saveProfile?: () => Promise<void> | void;
    template?: (args: DialogTemplateArgs) => React.ReactElement | null;
}

interface SaveDialogState {
    disabled: boolean;
}

class SaveDialog extends Component<SaveDialogProps, SaveDialogState> {
    state: SaveDialogState = {disabled: false};

    constructor(props: SaveDialogProps) {
        super(props);
        this.save = this.save.bind(this);
    }

    componentDidUpdate(prevProps: SaveDialogProps){
        if(!prevProps.open && this.props.open && this.state.disabled){
            this.setState({disabled: false});
        }
    }
    save = async (values?: Profile) => {
        let {onSave, profileType, format, shared, saveProfile} = this.props;
        this.setState({disabled: true});
        if(saveProfile){
            await saveProfile();
        } else {
            if(format){
                values = format(values);
            }
            if(shared){
                await saveAsSharedProfile(values, profileType, false);
            } else {
                await save(values, profileType);
            }
        }
        if(onSave){
            onSave();
        }
    }
    render(){
        let {values, open, onClose, type} = this.props;
        let disabled = this.state.disabled;
        type = type || 'profile';
        if(this.props.template)
            return this.props.template({values, open, onClose, disabled, save: this.save});

        return <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth={true}>
            <Toolbar className='lbtoolbar'>Confirm Save</Toolbar>
            <DialogContent>
            <Typography>
            {`Are you sure you want to save this ${type}?`}
                {values.Shared && ` This ${type} is shared with your trading partners and will be updated for them as well.`}
                {values.Id && !values.Shared && ` This ${type} is shared with other users at your company and will be updated for them as well.`}
            </Typography>
            </DialogContent>
            <DialogActions>
                <Button variant="text" color="primary" disabled={disabled} onClick={onClose}>No</Button>
                <Button variant="text" color='primary' disabled={disabled} onClick={() => this.save(values)}>Yes</Button>
            </DialogActions>
        </Dialog>;
    }
}

interface SaveAsDialogProps {
    open?: boolean;
    onClose?: (event?: any, reason?: string) => void;
    onSaveAs?: (newName: string, shareType: string) => void;
    values?: Profile;
    profileType?: ProfileType;
    format?: (values: Profile) => Profile;
    shared?: boolean;
    saveProfile?: (newName: string, shareType: string) => Promise<void> | void;
    title?: string;
    placeholder?: string;
    allowShare?: boolean;
    existingNames?: string[];
    template?: (args: DialogTemplateArgs) => React.ReactElement | null;
}

interface SaveAsDialogState {
    disabled: boolean;
    newProfileName: string;
    shareType: string;
}

class SaveAsDialog extends Component<SaveAsDialogProps, SaveAsDialogState> {
    state: SaveAsDialogState = {disabled: false, newProfileName: '', shareType: 'private'};

    constructor(props: SaveAsDialogProps) {
        super(props);
        this.saveAs = this.saveAs.bind(this);
        this.setState = this.setState.bind(this);
    }

    componentDidUpdate(prevProps: SaveAsDialogProps){
        if(!prevProps.open && this.props.open && this.state.disabled){
            this.setState({disabled: false, shareType: 'private', newProfileName: ''});
        }
    }
    saveAs = async () => {
        let {values, profileType, onSaveAs, format, shared, saveProfile} = this.props;
        let newName = this.state.newProfileName;
        this.setState({disabled: true});
        if(saveProfile){
            await saveProfile(newName, this.state.shareType);
        }else{
            values = {...values};
            if(format){
                values = format(values);
            }
            delete values.Id;
            values.Name = newName;
            if(shared){
                await saveAsSharedProfile(values, profileType, false);
            } else {
                await save(values, profileType);
            }
        }
        if(onSaveAs){
            onSaveAs(newName, this.state.shareType);
        }
    }

    render(){
        let {open, onClose, title, placeholder, allowShare, existingNames} = this.props;

        if(this.props.template)
            return this.props.template({open, onClose, saveAs: this.saveAs, setState: this.setState, state: this.state});

        return <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth={true}>
            <Toolbar className='lbtoolbar'>{title || 'Save Profile As'}</Toolbar>
            <DialogContent style={{display: 'flex', flexDirection: 'column', gap: '0.5em'}}>
                <TextField style={{width: '100%'}} placeholder={placeholder || 'New profile name'} value={this.state.newProfileName} onChange={(e) => this.setState({newProfileName: e.target.value})}/>
                {allowShare && <Select style={{width: '100%'}} value={this.state.shareType} onChange={e => this.setState({shareType: e.target.value})}>
                    <MenuItem value='private'>Visible only to me</MenuItem>
                    <MenuItem value='company'>Visible to all users on my team</MenuItem>
                    <MenuItem value='partners'>Visible to all users on my team and all trading partners</MenuItem>
                </Select>}
            </DialogContent>
            <DialogActions><Button variant="text" color='primary' disabled={!this.state.newProfileName || this.state.disabled || (existingNames && existingNames.indexOf(this.state.newProfileName) > -1)} onClick={this.saveAs}>Save</Button></DialogActions>
        </Dialog>;
    }
}

export { ShareDialog, DeleteDialog, SaveDialog, SaveAsDialog, deleteProfile, addExportProfile, loadProfilesWithDescriptions as loadProfiles, save as saveProfile, saveAsSharedProfile };
